import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

/**
 * ModuleShellErrorBoundary — boundary no nível do shell do módulo.
 *
 * Garante que um erro de renderização em qualquer página do workspace
 * (dashboards, lazy chunks, gráficos, payloads inesperados) nunca:
 *   1. derrube o app inteiro (sidebar/topbar permanecem visíveis), nem
 *   2. deixe a tela em branco.
 *
 * Renderiza um cartão de erro amigável com "Tentar novamente" e um
 * escape hatch para o Dashboard Geral. Sem logout automático.
 */
interface ModuleShellErrorBoundaryProps {
  children: React.ReactNode;
  /** Rótulo do módulo ativo (aparece na mensagem de erro). */
  moduleName?: string;
}

interface ModuleShellErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ModuleShellErrorBoundary extends Component<
  ModuleShellErrorBoundaryProps,
  ModuleShellErrorBoundaryState
> {
  state: ModuleShellErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ModuleShellErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Diagnóstico no console sem derrubar o shell (sem reload, sem logout).
    console.error("[ModuleShellErrorBoundary]", error, info.componentStack);
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const moduleName = this.props.moduleName ? ` (${this.props.moduleName})` : "";
    const errorMessage = this.state.error?.message;

    return (
      <div className="flex min-h-[50vh] items-center justify-center p-6">
        <div className="w-full max-w-md rounded-xl border border-amber-200 bg-amber-50/60 p-6 text-center shadow-sm">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-amber-100">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          </div>
          <h2 className="mt-3 text-sm font-bold text-slate-900">
            Não foi possível exibir este painel{moduleName}
          </h2>
          <p className="mt-1 text-xs leading-relaxed text-slate-500">
            Ocorreu um erro inesperado ao renderizar esta página. O restante do
            workspace continua disponível.
          </p>
          {errorMessage ? (
            <p
              className="mt-2 truncate rounded-md bg-white/70 px-2 py-1 font-mono text-[10px] text-slate-400"
              title={errorMessage}
            >
              {errorMessage}
            </p>
          ) : null}
          <div className="mt-4 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={this.handleRetry}
              className="h-8 rounded-lg bg-primary px-4 text-[11px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Tentar novamente
            </button>
            <a
              href="/geral"
              className="inline-flex h-8 items-center rounded-lg border border-slate-200 bg-white px-4 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-50"
            >
              Ir para o Dashboard Geral
            </a>
          </div>
        </div>
      </div>
    );
  }
}